import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Save, Terminal, Play, Eye, RefreshCw, ExternalLink, Loader2, Box, Database, Globe, ScrollText, Train } from 'lucide-react';
import Editor from '@monaco-editor/react';
import axios from 'axios';
import { toast } from 'sonner';
import FileExplorer from './FileExplorer';
import DatabasePanel from './DatabasePanel';
import DomainsPanel from './DomainsPanel';
import LogsPanel from './LogsPanel';
import RailwayPanel from './RailwayPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const ProjectIDE = ({ project, onClose }) => {
  const [openFiles, setOpenFiles] = useState([]);
  const [activeFile, setActiveFile] = useState(null);
  const [unsaved, setUnsaved] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [showTerm, setShowTerm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const [projectType, setProjectType] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [termLogs, setTermLogs] = useState([]);
  const [showDatabase, setShowDatabase] = useState(false);
  const [showDomains, setShowDomains] = useState(false);
  const [showRailway, setShowRailway] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // 'railway' | 'database' | 'domains' | 'logs' | null
  const contents = useRef({});
  const previewRef = useRef(null);

  // Detect project type on load
  useEffect(() => {
    if (project?.id) detectProjectType();
  }, [project?.id]);

  const detectProjectType = async () => {
    try {
      const token = localStorage.getItem('cloudtexa_token');
      const res = await axios.get(`${API}/api/build/detect/${project.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setProjectType(res.data);
      addLog('info', `Proyecto detectado: ${res.data.icon} ${res.data.name}`);
    } catch (e) { console.error(e); }
  };

  const addLog = (type, message) => {
    const ts = new Date().toLocaleTimeString();
    setTermLogs(prev => [...prev, { type, message, time: ts }]);
  };

  const handleSelect = useCallback((file) => {
    if (!file || file.is_directory) return;
    contents.current[file.id] = file.content || '';
    const idx = openFiles.findIndex(f => f.id === file.id);
    if (idx >= 0) { setActiveFile(openFiles[idx]); return; }
    const newF = { ...file, orig: file.content || '' };
    setOpenFiles(prev => [...prev, newF]);
    setActiveFile(newF);
  }, [openFiles]);

  const handleChange = (val) => {
    if (!activeFile) return;
    contents.current[activeFile.id] = val;
    setUnsaved(prev => { const n = new Set(prev); val !== activeFile.orig ? n.add(activeFile.id) : n.delete(activeFile.id); return n; });
    setOpenFiles(prev => prev.map(f => f.id === activeFile.id ? { ...f, content: val } : f));
    setActiveFile(prev => prev ? { ...prev, content: val } : null);
  };

  const saveFile = async (f = activeFile) => {
    if (!f) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.put(`${API}/api/files/${f.id}`, { content: contents.current[f.id] || f.content }, { headers: { Authorization: `Bearer ${token}` } });
      setOpenFiles(prev => prev.map(x => x.id === f.id ? { ...x, orig: contents.current[f.id] || f.content } : x));
      setUnsaved(prev => { const n = new Set(prev); n.delete(f.id); return n; });
      toast.success(`Guardado: ${f.name}`);
      addLog('success', `Guardado: ${f.name}`);
    } catch (e) { toast.error('Error'); addLog('error', 'Error al guardar'); }
    setSaving(false);
  };

  const saveAll = async () => {
    const toSave = openFiles.filter(f => unsaved.has(f.id));
    for (const f of toSave) await saveFile(f);
  };

  const closeTab = (id, e) => {
    e?.stopPropagation();
    if (unsaved.has(id) && !window.confirm('¿Descartar cambios?')) return;
    const newF = openFiles.filter(f => f.id !== id);
    setOpenFiles(newF);
    delete contents.current[id];
    setUnsaved(prev => { const n = new Set(prev); n.delete(id); return n; });
    if (activeFile?.id === id) setActiveFile(newF.length > 0 ? newF[newF.length - 1] : null);
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    addLog('info', 'Generando preview...');
    
    // Save all files first
    if (unsaved.size > 0) {
      addLog('info', 'Guardando archivos...');
      await saveAll();
    }
    
    try {
      const token = localStorage.getItem('cloudtexa_token');
      const res = await axios.post(`${API}/api/build/preview/${project.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      
      const url = `${API}${res.data.preview_url}`;
      setPreviewUrl(url);
      setShowPreview(true);
      addLog('success', `Preview listo: ${res.data.project_type.icon} ${res.data.project_type.name}`);
      addLog('info', `${res.data.file_count} archivos procesados`);
      
      // Refresh iframe
      if (previewRef.current) {
        previewRef.current.src = url;
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Error al generar preview';
      toast.error(msg);
      addLog('error', msg);
    }
    setPreviewLoading(false);
  };

  const refreshPreview = () => {
    if (previewRef.current && previewUrl) {
      previewRef.current.src = previewUrl + '?t=' + Date.now();
      addLog('info', 'Preview actualizado');
    }
  };

  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); saveFile(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') { e.preventDefault(); runPreview(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [activeFile]);

  return (
    <div className={`fixed inset-0 bg-slate-950 z-50 flex flex-col`} data-testid="project-ide">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <h2 className="font-medium text-white">{project?.name || 'Editor'}</h2>
          {projectType && <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-gray-300">{projectType.icon} {projectType.name}</span>}
          {unsaved.size > 0 && <span className="text-xs text-amber-400">({unsaved.size} sin guardar)</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runPreview} disabled={previewLoading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 text-sm hover:bg-green-500/30 disabled:opacity-50" title="Ctrl+B">
            {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Preview
          </button>
          <button onClick={saveAll} disabled={saving || unsaved.size === 0} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-400 text-sm disabled:opacity-50">
            <Save className="w-4 h-4" />Guardar
          </button>
          <div className="w-px h-6 bg-slate-700" />
          <button onClick={() => setShowPreview(!showPreview)} className={`p-2 rounded-lg ${showPreview ? 'bg-blue-500/20 text-blue-400' : 'text-gray-400 hover:bg-white/10'}`} title="Toggle Preview">
            <Eye className="w-4 h-4" />
          </button>
          <button onClick={() => { setActivePanel(activePanel === 'railway' ? null : 'railway'); setShowRailway(activePanel !== 'railway'); }} className={`p-2 rounded-lg ${activePanel === 'railway' ? 'bg-purple-500/20 text-purple-400' : 'text-gray-400 hover:bg-white/10'}`} title="Desplegar en Railway" data-testid="railway-toggle-btn">
            <Train className="w-4 h-4" />
          </button>
          <button onClick={() => { setActivePanel(activePanel === 'database' ? null : 'database'); setShowDatabase(activePanel !== 'database'); }} className={`p-2 rounded-lg ${activePanel === 'database' ? 'bg-emerald-500/20 text-emerald-400' : 'text-gray-400 hover:bg-white/10'}`} title="Base de Datos" data-testid="database-toggle-btn">
            <Database className="w-4 h-4" />
          </button>
          <button onClick={() => { setActivePanel(activePanel === 'domains' ? null : 'domains'); setShowDomains(activePanel !== 'domains'); }} className={`p-2 rounded-lg ${activePanel === 'domains' ? 'bg-pink-500/20 text-pink-400' : 'text-gray-400 hover:bg-white/10'}`} title="Dominios" data-testid="domains-toggle-btn">
            <Globe className="w-4 h-4" />
          </button>
          <button onClick={() => { setActivePanel(activePanel === 'logs' ? null : 'logs'); }} className={`p-2 rounded-lg ${activePanel === 'logs' ? 'bg-orange-500/20 text-orange-400' : 'text-gray-400 hover:bg-white/10'}`} title="Logs" data-testid="logs-toggle-btn">
            <ScrollText className="w-4 h-4" />
          </button>
          <button onClick={() => setShowTerm(!showTerm)} className={`p-2 rounded-lg ${showTerm ? 'bg-purple-500/20 text-purple-400' : 'text-gray-400 hover:bg-white/10'}`} title="Toggle Terminal">
            <Terminal className="w-4 h-4" />
          </button>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-red-500/20 hover:text-red-400">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* File Explorer */}
        <div style={{ width: 240 }} className="flex-shrink-0">
          <FileExplorer projectId={project?.id} projectName={project?.name} onFileSelect={handleSelect} selectedFile={activeFile} />
        </div>

        {/* Editor + Preview */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="flex items-center bg-slate-800/50 border-b border-slate-700 overflow-x-auto">
            {openFiles.map((f) => (
              <div key={f.id} onClick={() => setActiveFile(f)} className={`flex items-center gap-2 px-3 py-2 border-r border-slate-700 cursor-pointer text-sm ${activeFile?.id === f.id ? 'bg-slate-900 text-white' : 'text-gray-400 hover:text-white'}`}>
                {unsaved.has(f.id) && <span className="text-amber-400">●</span>}
                <span className="truncate max-w-[100px]">{f.name}</span>
                <button onClick={(e) => closeTab(f.id, e)} className="p-0.5 rounded hover:bg-white/10"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>

          {/* Editor and Preview Split */}
          <div className="flex-1 flex overflow-hidden">
            {/* Editor Panel */}
            <div className={`flex-1 flex flex-col overflow-hidden ${showPreview ? 'w-1/2' : 'w-full'}`}>
              {activeFile ? (
                <Editor height="100%" language={activeFile.language || 'plaintext'} value={contents.current[activeFile.id] || activeFile.content || ''} onChange={handleChange} theme="vs-dark" options={{ fontSize: 13, minimap: { enabled: !showPreview }, scrollBeyondLastLine: false, automaticLayout: true, tabSize: 2, wordWrap: 'on', padding: { top: 12 } }} />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 flex-col gap-2">
                  <Box className="w-12 h-12 text-gray-600" />
                  <p>Selecciona un archivo para editar</p>
                  <p className="text-xs">o extrae código del chat con el botón ↓</p>
                </div>
              )}
            </div>

            {/* Preview Panel */}
            {showPreview && !activePanel && (
              <div className="w-1/2 border-l border-slate-700 flex flex-col bg-white">
                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 border-b border-slate-700">
                  <span className="text-xs text-gray-400 font-medium flex items-center gap-2">
                    <Eye className="w-3 h-3" /> PREVIEW
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={refreshPreview} className="p-1 text-gray-400 hover:text-white"><RefreshCw className="w-3 h-3" /></button>
                    {previewUrl && <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="p-1 text-gray-400 hover:text-white"><ExternalLink className="w-3 h-3" /></a>}
                  </div>
                </div>
                {previewUrl ? (
                  <iframe ref={previewRef} src={previewUrl} className="flex-1 w-full bg-white" title="Preview" sandbox="allow-scripts allow-same-origin" />
                ) : (
                  <div className="flex-1 flex items-center justify-center bg-slate-900 text-gray-500 flex-col gap-2">
                    <Play className="w-8 h-8" />
                    <p className="text-sm">Click en "Preview" para ver tu app</p>
                    <p className="text-xs text-gray-600">o presiona Ctrl+B</p>
                  </div>
                )}
              </div>
            )}

            {/* Railway Panel - Despliegue Principal */}
            {activePanel === 'railway' && (
              <div className="w-1/2 border-l border-slate-700 flex flex-col">
                <RailwayPanel projectId={project?.id} onClose={() => setActivePanel(null)} />
              </div>
            )}

            {/* Database Panel */}
            {activePanel === 'database' && (
              <div className="w-1/2 border-l border-slate-700 flex flex-col">
                <DatabasePanel projectId={project?.id} onClose={() => setActivePanel(null)} />
              </div>
            )}

            {/* Domains Panel */}
            {activePanel === 'domains' && (
              <div className="w-1/2 border-l border-slate-700 flex flex-col">
                <DomainsPanel projectId={project?.id} onClose={() => setActivePanel(null)} />
              </div>
            )}

            {/* Logs Panel */}
            {activePanel === 'logs' && (
              <div className="w-1/2 border-l border-slate-700 flex flex-col">
                <LogsPanel projectId={project?.id} onClose={() => setActivePanel(null)} />
              </div>
            )}
          </div>

          {/* Terminal Panel */}
          {showTerm && (
            <div className="h-[160px] bg-black border-t border-slate-700 flex flex-col">
              <div className="flex items-center justify-between px-3 py-1 bg-slate-800/50 border-b border-slate-700">
                <span className="text-xs text-gray-400 font-medium">TERMINAL</span>
                <button onClick={() => setTermLogs([])} className="text-xs text-gray-500 hover:text-white">Limpiar</button>
              </div>
              <div className="flex-1 p-2 font-mono text-xs overflow-y-auto">
                {termLogs.length === 0 ? (
                  <p className="text-gray-600">Listo. Usa Ctrl+S para guardar, Ctrl+B para preview.</p>
                ) : (
                  termLogs.map((log, i) => (
                    <div key={i} className={`flex gap-2 ${log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-green-400' : log.type === 'warning' ? 'text-yellow-400' : 'text-gray-400'}`}>
                      <span className="text-gray-600">[{log.time}]</span>
                      <span>{log.message}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-between px-4 py-1 bg-slate-900 border-t border-slate-700 text-xs text-gray-500">
        <div className="flex items-center gap-4">
          <span>{activeFile?.path || 'Sin archivo'}</span>
          {activeFile && <span>{activeFile.language || 'text'}</span>}
        </div>
        <div className="flex items-center gap-4">
          {projectType?.can_preview && <span className="text-green-500">● Preview disponible</span>}
          <span>UTF-8</span>
          {saving && <span className="text-cyan-400">Guardando...</span>}
        </div>
      </div>
    </div>
  );
};

export default ProjectIDE;
